import { createFileRoute, useNavigate, useRouter } from "@tanstack/react-router";
import { z } from "zod";
import { PreFlightHubPage } from "@/features/preflight/pages/PreFlightHubPage";
import type { OpenPreflightReportOptions } from "@/features/preflight/lib/report-open";

declare module "@tanstack/react-router" {
  interface HistoryState {
    /** Marks the entry a Quick view pushed, so closing it pops that entry instead of adding a dead Back. */
    preflightPeekPushed?: boolean;
  }
}

export const Route = createFileRoute("/_authenticated/pre-flight/")({
  validateSearch: z.object({
    /** Report open in the side peek — in the URL so it survives reload and can be shared. */
    peek: z.string().min(1).optional().catch(undefined),
  }),
  component: PreFlightHubRoute,
});

function PreFlightHubRoute() {
  const navigate = useNavigate({ from: Route.fullPath });
  const router = useRouter();
  const { peek } = Route.useSearch();

  function openReport(reportId: string, options?: OpenPreflightReportOptions) {
    const mode = options?.mode ?? "page";
    if (mode === "new-tab") {
      const { href } = router.buildLocation({
        to: "/pre-flight/reports/$reportId",
        params: { reportId },
      });
      window.open(href, "_blank", "noopener,noreferrer");
      return;
    }
    if (mode === "peek") {
      // Opening pushes a marked entry, so browser Back closes the peek; stepping J/K replaces
      // and keeps that entry's state, so Back does not replay every report stepped through.
      void navigate({
        search: (prev) => ({ ...prev, peek: reportId }),
        replace: peek != null,
        state:
          peek != null
            ? true
            : (prev) => ({ ...prev, preflightPeekPushed: true }),
      });
      return;
    }
    void navigate({
      to: "/pre-flight/reports/$reportId",
      params: { reportId },
      search: {},
    });
  }

  /**
   * A peek this hub pushed closes by popping its entry, so the next Back leaves the hub. A peek
   * opened from a shared or pasted `?peek=` URL has no entry of ours beneath it to pop. History
   * state survives a reload, so a reloaded peek still pops.
   */
  function closePeek() {
    if (router.state.location.state.preflightPeekPushed) {
      router.history.back();
      return;
    }
    void navigate({
      search: (prev) => ({ ...prev, peek: undefined }),
      replace: true,
    });
  }

  return (
    <PreFlightHubPage
      onOpenReport={openReport}
      peekReportId={peek ?? null}
      onClosePeek={closePeek}
    />
  );
}
